import { useCallback, useState } from "react";

import type { FilterSpec, FilterType, UiOperator } from "./types";
import { useColumnFilter } from "./useColumnFilter";

export interface UseColumnFilterPopoverParams {
  columnId: string;
  filterType: FilterType;
  spec: FilterSpec | null;
  onChange: (spec: FilterSpec | null) => void;
  operators?: UiOperator[];
}

export interface UseColumnFilterPopoverReturn {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;

  operator: ReturnType<typeof useColumnFilter>["operator"];
  setOperator: ReturnType<typeof useColumnFilter>["setOperator"];
  operatorOptions: ReturnType<typeof useColumnFilter>["operatorOptions"];

  value: ReturnType<typeof useColumnFilter>["value"];
  setValue: ReturnType<typeof useColumnFilter>["setValue"];
  value2: ReturnType<typeof useColumnFilter>["value2"];
  setValue2: ReturnType<typeof useColumnFilter>["setValue2"];
  isValueDisabled: ReturnType<typeof useColumnFilter>["takesNoValue"];
  isRangeOperator: ReturnType<typeof useColumnFilter>["usesRangeValue"];

  join: ReturnType<typeof useColumnFilter>["join"];
  setJoin: ReturnType<typeof useColumnFilter>["setJoin"];
  secondOperator: ReturnType<typeof useColumnFilter>["secondOperator"];
  setSecondOperator: ReturnType<typeof useColumnFilter>["setSecondOperator"];
  secondValue: ReturnType<typeof useColumnFilter>["secondValue"];
  setSecondValue: ReturnType<typeof useColumnFilter>["setSecondValue"];
  secondValue2: ReturnType<typeof useColumnFilter>["secondValue2"];
  setSecondValue2: ReturnType<typeof useColumnFilter>["setSecondValue2"];
  showSecond: ReturnType<typeof useColumnFilter>["showSecond"];
  secondUsesValue: ReturnType<typeof useColumnFilter>["secondUsesValue"];
  secondUsesRangeValue: ReturnType<
    typeof useColumnFilter
  >["secondUsesRangeValue"];

  commitAndClose: () => void;
  cancelAndClose: () => void;
}

export function useColumnFilterPopover({
  columnId,
  filterType,
  spec,
  onChange,
  operators,
}: UseColumnFilterPopoverParams): UseColumnFilterPopoverReturn {
  const [isOpen, setIsOpen] = useState(false);

  const {
    operator,
    setOperator,
    value,
    setValue,
    value2,
    setValue2,
    operatorOptions,
    takesNoValue: isValueDisabled,
    usesRangeValue: isRangeOperator,
    join,
    setJoin,
    secondOperator,
    setSecondOperator,
    secondValue,
    setSecondValue,
    secondValue2,
    setSecondValue2,
    showSecond,
    secondUsesValue,
    secondUsesRangeValue,
    buildSpec,
  } = useColumnFilter({ columnId, filterType, spec, isOpen, operators });

  const cancelAndClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Apply (button / Enter) is the only commit point. Closing any other way —
  // click-outside, Escape — discards edits: useColumnFilter re-syncs the
  // editor from the applied spec when the popover is closed.
  const commitAndClose = useCallback(() => {
    const next = buildSpec();
    if (next === undefined) {
      return; // invalid input — keep the popover open
    }
    onChange(next);
    setIsOpen(false);
  }, [buildSpec, onChange]);

  return {
    isOpen,
    setIsOpen,
    operator,
    setOperator,
    value,
    setValue,
    value2,
    setValue2,
    operatorOptions,
    isValueDisabled,
    isRangeOperator,
    join,
    setJoin,
    secondOperator,
    setSecondOperator,
    secondValue,
    setSecondValue,
    secondValue2,
    setSecondValue2,
    showSecond,
    secondUsesValue,
    secondUsesRangeValue,
    commitAndClose,
    cancelAndClose,
  };
}
